import React, { useMemo, useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Checkbox, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import { PlusIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import CustomDataGrid from "../../../components/elements/CustomDataGrid";
import ControlledInput from "../../../components/elements/ControlledInput";
import ControlledReactQuill from "../../../components/elements/ControlledReactQuill";
import { getFetcher, postWithFormDataFetcher, putWithFormDataFetcher, deleteFetcher } from "../../../services/api";
import RootAdmin from "../../../components/layouts/RootAdmin";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import ModalAlert from "../../../components/elements/ModalAlert";
import ControlledReactSelect from "../../../components/elements/ControlledReactSelect";

export default function AnnouncementPage() {
  /*
   * Tabel ini memakai paginasi sisi server, jadi nomor halaman harus ikut ke
   * dalam alamat DAN dikembalikan ke tabel lewat prop page/pageSize.
   *
   * Sebelumnya alamatnya tetap "/api/announcements/search" dan page/setPage
   * tidak pernah dikirim ke CustomDataGrid - menekan tombol halaman berikutnya
   * memanggil setPage yang undefined. Selama pengumumannya belum lebih dari
   * sepuluh hal ini tidak pernah terlihat.
   */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, mutate } = useSWR(`/api/announcements/search?page=${page + 1}&limit=${pageSize}`, getFetcher);

  const [isEdit, setIsEdit] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenModalDeleteAlert, setIsOpenModalDeleteAlert] = useState(false);
  const [photo, setPhoto] = useState(null); // Menyimpan file gambar yang diunggah
  const [lampiran, setLampiran] = useState(null); // Berkas yang bisa diunduh pembaca pengumuman

  const {
    control,
    handleSubmit,
    reset,
    register,
    watch,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      type: "",
      isPublic: null,
      isActive: null,
    },
  });
  const type = watch("type"); // Watch for type changes
  const handleOpenModal = () => setIsModalOpen(true);

  useEffect(() => {
    if (selectedAnnouncement) {
      reset({
        title: selectedAnnouncement.title || "",
        description: selectedAnnouncement.description || "",
        type: selectedAnnouncement.type || "",
        isPublic: selectedAnnouncement.isPublic || false,
        isActive: selectedAnnouncement.isActive || true,
        // Kolom tanggal memakai <input type="date">, jadi nilainya harus
        // yyyy-MM-dd; nilai dari server berbentuk ISO lengkap.
        tanggalBerlaku: selectedAnnouncement.tanggalBerlaku ? String(selectedAnnouncement.tanggalBerlaku).slice(0, 10) : "",
      });
    }
  }, [selectedAnnouncement, reset]);

  const handleCloseModal = () => {
    reset();
    setSelectedAnnouncement(null);
    setPhoto(null);
    setLampiran(null);
    setIsEdit(false);
    setIsModalOpen(false);
  };

  const handleCreateOrUpdate = async (formData) => {
    try {
      const formDataObj = new FormData();

      /*
       * Muatan disusun kolom per kolom, bukan dengan menyalin seluruh isi form.
       * Dua sebab:
       *   - tanggal kosong tidak boleh ikut terkirim, sebab validasi tanggal di
       *     server menolak string kosong dan seluruh permintaan ikut gagal;
       *   - checkbox yang tidak dicentang menghasilkan "" pada react-hook-form,
       *     dan "" bukan false bagi server - tanpa pemaksaan ini pengumuman yang
       *     sengaja dibuat tidak-publik justru tersimpan sebagai publik.
       */
      formDataObj.append("title", formData.title ?? "");
      formDataObj.append("type", formData.type ?? "");
      if (formData.description) formDataObj.append("description", formData.description);
      if (formData.tanggalBerlaku) formDataObj.append("tanggalBerlaku", formData.tanggalBerlaku);
      formDataObj.append("isActive", formData.isActive ? "true" : "false");
      formDataObj.append("isPublic", formData.isPublic ? "true" : "false");

      // Menambahkan foto jika ada
      if (photo) {
        formDataObj.append("photos", photo);
      }

      // Lampiran yang bisa diunduh pembaca - hanya dikirim bila dipilih, supaya
      // menyunting judul tidak menghapus lampiran yang sudah ada.
      if (lampiran) {
        formDataObj.append("lampiran", lampiran);
      }

      let response;
      if (isEdit) {
        response = await putWithFormDataFetcher(`/api/announcements/update`, selectedAnnouncement.id, formDataObj);
      } else {
        response = await postWithFormDataFetcher("/api/announcements/create", formDataObj);
      }

      // Periksa respons error
      if (response?.status >= 400) {
        throw new Error(response?.data?.message || "Terjadi kesalahan saat memproses pengumuman.");
      }

      toast.success(isEdit ? "Pengumuman berhasil diperbarui!" : "Pengumuman berhasil dibuat!");
      mutate(); // Refresh data
      handleCloseModal();
    } catch (error) {
      toast.error(error.message || "Gagal menyimpan pengumuman.");
    }
  };

  const handleDelete = async (id) => {
    console.log("Deleting announcement with ID:", id);
    try {
      await deleteFetcher(`/api/announcements/delete`, id);
      toast.success("Pengumuman berhasil dihapus!");
      mutate(); // Refresh data
      setIsOpenModalDeleteAlert(false);
    } catch (error) {
      toast.error("Gagal menghapus pengumuman.");
      setIsOpenModalDeleteAlert(false);
    }
  };

  const handleOpenModalDeleteAlert = (id) => {
    setDeleteId(id);
    setIsOpenModalDeleteAlert(true);
  };

  const handleEdit = (announcement) => {
    setSelectedAnnouncement(announcement);
    setPhoto(null);
    setLampiran(null);
    setIsEdit(true);
    handleOpenModal();
  };

  const columns = useMemo(
    () => [
      { field: "title", headerName: "Judul" },
      { field: "type", headerName: "Tipe" },
      {
        field: "tanggalBerlaku",
        headerName: "Tanggal",
        renderCell: (params) => {
          const nilai = params.row.tanggalBerlaku || params.row.createdAt;
          return nilai ? new Date(nilai).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "-";
        },
      },
      {
        field: "lampiranNama",
        headerName: "Lampiran",
        renderCell: (params) => params.row.lampiranNama || "-",
      },
      { field: "isActive", headerName: "Aktif", renderCell: (params) => (params.row.isActive ? "Ya" : "Tidak") },
      {
        field: "actions",
        headerName: "Aksi",
        renderCell: (params) => (
          <div className="flex gap-2">
            <Button color="primary" isIconOnly size="sm" onPress={() => handleEdit(params.row)}>
              <PencilSquareIcon className="size-4" />
            </Button>
            <Button color="danger" isIconOnly size="sm" onPress={() => handleOpenModalDeleteAlert(params.row.id)}>
              <TrashIcon className="size-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardHeader>Kelola Pengumuman</CardHeader>
        <Divider />
        <CardBody>
          <div className="mb-5">
            <Button onPress={handleOpenModal} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
              Tambah Pengumuman
            </Button>
          </div>
          <div className="h-[500px]">
            <CustomDataGrid data={data?.data || []} rowCount={data?.total || 0} isLoading={isLoading} columns={columns} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />
          </div>
        </CardBody>
      </Card>

      <Modal size="lg" isOpen={isModalOpen} onOpenChange={setIsModalOpen} onClose={handleCloseModal} isDismissable={!isSubmitting}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{isEdit ? "Edit Pengumuman" : "Tambah Pengumuman"}</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit(handleCreateOrUpdate)}>
                  <div className="grid grid-cols-1 gap-3 mb-5">
                    <ControlledReactSelect
                      /*
                       * Hanya dua tipe yang benar-benar punya tempat tampil:
                       * Banner muncul di kotak pengumuman, Running Text di teks
                       * berjalan bilah atas. Pilihan "Informasi" (Non Banner)
                       * dihapus karena tidak pernah ditampilkan di mana pun -
                       * pengumuman yang dibuat dengan tipe itu hanya tersimpan.
                       * Data lama bertipe Non Banner dibiarkan apa adanya.
                       */
                      options={[
                        { label: "Banner", value: "Banner" },
                        { label: "Running Text", value: "Running Text" },
                      ]}
                      label="Tipe"
                      name="type"
                      control={control}
                      isRequired
                      defaultValue={selectedAnnouncement?.type || ""}
                    />
                    <ControlledInput label="Judul" name="title" control={control} isRequired defaultValue={selectedAnnouncement?.title || ""} />
                    {type !== "Running Text" && <ControlledReactQuill label="Deskripsi" name="description" control={control} defaultValue={selectedAnnouncement?.description || ""} />}

                    {type !== "Running Text" && (
                      <div>
                        <label className="block text-sm font-medium text-ink-700 mb-2">Poster atau Gambar Pengumuman</label>
                        {selectedAnnouncement?.url && !photo && <img src={`${process.env.REACT_APP_API_BASE_URL}/${selectedAnnouncement.url}`} alt="Current Poster" className="w-20 h-20 object-cover rounded-md mb-3" />}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setPhoto(e.target.files[0])}
                          className="block w-full text-sm text-ink-500
                                                      file:mr-4 file:py-2 file:px-4
                                                      file:rounded-full file:border-0
                                                      file:text-sm file:font-semibold
                                                      file:bg-primary-100 file:text-primary-700
                                                      hover:file:bg-primary-200"
                        />
                      </div>
                    )}

                    <ControlledInput
                      label="Tanggal Pengumuman"
                      name="tanggalBerlaku"
                      type="date"
                      control={control}
                      description="Tanggal yang tampil di samping pengumuman. Kosongkan untuk memakai tanggal pembuatan."
                    />

                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-2">Lampiran (PDF/gambar, bisa diunduh pembaca)</label>
                      {selectedAnnouncement?.lampiranNama && !lampiran && (
                        <p className="text-xs text-ink-500 mb-2">
                          Lampiran saat ini: <span className="text-ink-700">{selectedAnnouncement.lampiranNama}</span>. Biarkan kosong bila tidak ingin menggantinya.
                        </p>
                      )}
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={(e) => setLampiran(e.target.files[0])}
                        className="block w-full text-sm text-ink-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200"
                      />
                    </div>

                    <Checkbox {...register("isActive")} defaultChecked={selectedAnnouncement?.isActive}>
                      Aktif
                    </Checkbox>
                    {type !== "Running Text" && (
                      <Checkbox {...register("isPublic")} defaultChecked={selectedAnnouncement?.isPublic}>
                        Pengumuman Publik
                      </Checkbox>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="submit" color="primary" isLoading={isSubmitting} disabled={isSubmitting}>
                      {isEdit ? "Perbarui" : "Tambah"}
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <ModalAlert heading="Apakah Anda Yakin Menghapus Pengumuman?" buttonSubmitText="Ya" icon="warning" onSubmit={() => handleDelete(deleteId)} isOpen={isOpenModalDeleteAlert} onOpenChange={setIsOpenModalDeleteAlert} />
    </RootAdmin>
  );
}
